import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  MessageSquare, Phone, Mail, MapPin, Calendar, Loader2,
  CheckCircle2, Archive, Filter, ExternalLink, FileText, ArrowRight,
} from "lucide-react";

const STATUS_FILTERS = ["all", "new", "responded", "converted", "archived"];

// Every recognized value booking_inquiries.source is ever written with
// (discovery/public_router.py's Discover Rentals form, external_websites'
// website-integration widget/hosted inquiry). Anything else: legacy rows,
// future sources not yet labeled here: falls back to a neutral "Inquiry"
// rather than claiming a source we can't confirm.
const SOURCE_LABELS = {
  discover_rentals: "Discover Rentals",
  website_integration: "Website",
};
const sourceLabel = (source) => SOURCE_LABELS[source] || "Inquiry";

export default function Inquiries() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(filter === "all" ? "/inquiries" : `/inquiries?status=${filter}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/inquiries/${id}`, { status });
      toast.success("Inquiry updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="inquiries-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Inquiries</h1>
        <p className="text-sm text-slate-600 mt-1">
          Inquiries from customers who found you on Discover Rentals or through your connected website: each is labeled with where it came from.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-slate-500" />
        {STATUS_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`inq-filter-${f}`}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
              filter === f ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="inq-empty">
          No inquiries yet. Once you enable your <a href="/app/settings" className="underline">public profile</a>, customers who find you on{" "}
          <a href="/rentals" target="_blank" rel="noreferrer" className="underline">Discover Rentals</a> or through your{" "}
          <a href="/app/settings/website-integrations" className="underline">connected website</a> can send inquiries here.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((q) => (
            <li key={q.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`inq-card-${q.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-semibold">{q.customer_name}</h3>
                    <StatusPill status={q.status} />
                    <SourcePill source={q.source} />
                  </div>
                  <div className="mt-1 text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{q.customer_email}</span>
                    {q.customer_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{q.customer_phone}</span>}
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{q.created_at?.slice(0, 10)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {q.status === "converted" && q.converted_booking_id ? (
                    <Link
                      to={`/app/bookings/${q.converted_booking_id}`}
                      data-testid={`inq-view-booking-${q.id}`}
                      className="text-xs px-2.5 py-1 rounded-md border border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 inline-flex items-center gap-1 font-medium"
                    >
                      <FileText className="w-3 h-3" />
                      View booking {q.converted_booking?.booking_reference || q.converted_booking_reference || ""}
                      <ArrowRight className="w-3 h-3" />
                    </Link>
                  ) : (
                    <>
                      <button
                        onClick={() => nav(`/app/bookings/new?from_inquiry=${q.id}`)}
                        data-testid={`inq-convert-${q.id}`}
                        className="text-xs px-2.5 py-1 rounded-md bg-[#1B4332] text-white hover:bg-[#143527] inline-flex items-center gap-1 font-medium"
                      >
                        <ArrowRight className="w-3 h-3" /> Convert to booking
                      </button>
                      {q.status !== "responded" && (
                        <button onClick={() => updateStatus(q.id, "responded")} data-testid={`inq-mark-responded-${q.id}`}
                                className="text-xs px-2.5 py-1 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Mark responded
                        </button>
                      )}
                      {q.status !== "archived" && (
                        <button onClick={() => updateStatus(q.id, "archived")} data-testid={`inq-archive-${q.id}`}
                                className="text-xs px-2.5 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
                          <Archive className="w-3 h-3" /> Archive
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {(q.preferred_vehicle || q.start_date || q.end_date || q.pickup_location) && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                  {q.preferred_vehicle && <KV label="Vehicle" value={q.preferred_vehicle} />}
                  {(q.start_date || q.end_date) && (
                    <KV label="Dates" value={`${q.start_date || "?"} → ${q.end_date || "?"}`} />
                  )}
                  {q.pickup_location && <KV label="Pickup" icon={MapPin} value={q.pickup_location} className="sm:col-span-2" />}
                </div>
              )}
              {q.notes && (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-line border-l-2 border-slate-200 pl-3">{q.notes}</p>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <a href={`mailto:${q.customer_email}`} className="text-xs text-[#1B4332] underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Reply by email
                </a>
                {q.customer_phone && (
                  <a href={`tel:${q.customer_phone}`} className="text-xs text-[#1B4332] underline inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Call
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SourcePill({ source }) {
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-medium"
          data-testid="inq-source-pill">
      {sourceLabel(source)}
    </span>
  );
}
function StatusPill({ status }) {
  const tone = status === "new" ? "bg-amber-100 text-amber-800 border-amber-200"
            : status === "responded" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
            : status === "converted" ? "bg-[#1B4332]/10 text-[#1B4332] border-[#1B4332]/30"
            : "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${tone}`}>{status}</span>;
}
function KV({ label, value, icon: Icon, className = "" }) {
  return (
    <div className={`text-sm ${className}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
      <div className="flex items-center gap-1 text-slate-700">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
        <span>{value}</span>
      </div>
    </div>
  );
}
